import { FC, useState } from 'react';
import Explosion from 'react-canvas-confetti/dist/presets/explosion';
import { useTranslation } from 'react-i18next';
import { FaThumbsDown, FaThumbsUp } from 'react-icons/fa';
import { sendSizingFeedback } from '../../api/generic/sizingFeedback';
import { useAuthContext } from '../../common/context/AuthContext';
import { trackEvent } from '../../common/utils/analytics';
import SecondaryButton from '../buttons/SecondaryButton';
import PrimaryParagraph from '../texts/PrimaryParagraph';
import PrimaryTitle from '../texts/PrimaryTitle';
import { SizeRecommandationProps } from './types';

const SizeRecommandationView: FC<SizeRecommandationProps> = (props) => {
	const { t } = useTranslation();
	const { isAuthenticated, token, uid } = useAuthContext();
	const [feedbackSent, setFeedbackSent] = useState<boolean | null>(null);

	const handleFeedback = async (feedback: boolean) => {
		trackEvent(feedback ? 'size_reco_like' : 'size_reco_dislike');
		try {
			await sendSizingFeedback(token, {
				user_id: uid,
				size_label: props.bestSize.size,
				product_url: window.location.href,
				feedback,
			});
			setFeedbackSent(feedback);
		} catch (error) {
			console.error('Failed to send sizing feedback', error);
		}
	};
	
	return (
		<div className={'pop-flex pop-flex-col pop-justify-center pop-items-center pop-my-10 pop-h-full pop-fadeIn'}>
			<PrimaryTitle className={'pop-text-3xl pop-text-center pop-my-4 pop-font-semibold'}>{t('user_size_search_result_title')}</PrimaryTitle>
			<div className={'pop-flex pop-flex-col pop-w-full pop-justify-center pop-items-center'}>
				<PrimaryParagraph className={'pop-text-huge pop-text-center pop-font-black pop-my-20'}>
					{props.bestSize.label !== 'error' ? props.bestSize.size : '–'}
				</PrimaryParagraph>
				{props.bestSize.label === 'error' && (
					<PrimaryParagraph className={'pop-text-lg pop-text-center pop-my-1 pop-font-semibold'}>
					{t('no_matching_size')}
					</PrimaryParagraph>
				)}
				{props.bestSize.label !== 'error' && (
				<Explosion autorun={{ speed: 2, duration: 1000 }} />
				)}
				{props.bestSize.label !== 'error' && isAuthenticated && (
					<div className={'pop-flex pop-justify-center pop-gap-3 pop-mt-4'}>
						<FaThumbsUp
							className={`pop-cursor-pointer pop-text-xl pop-text-popsizePrimary20 hover:pop-opacity-100 ${
								feedbackSent === null
									? 'pop-opacity-70'
									: feedbackSent === true
									? 'pop-opacity-100'
									: 'pop-opacity-30'
							}`}
							onClick={() => feedbackSent === null && handleFeedback(true)}
						/>
						<FaThumbsDown
							className={`pop-cursor-pointer pop-text-xl pop-text-popsizePrimary20 hover:pop-opacity-100 ${
								feedbackSent === null
									? 'pop-opacity-70'
									: feedbackSent === false
									? 'pop-opacity-100'
									: 'pop-opacity-30'
							}`}
							onClick={() => feedbackSent === null && handleFeedback(false)}
						/>
					</div>
				)}
				{feedbackSent !== null && (
					<div className={'pop-text-center pop-mt-2'}>
						<PrimaryParagraph className={'pop-text-sm'}>{t('thank_you_feedback')}</PrimaryParagraph>
						{feedbackSent === false && <PrimaryParagraph className={'pop-text-sm'}>{t('helps_us_get_better')}</PrimaryParagraph>}
					</div>
				)}
				<div className={'pop-flex pop-flex-col pop-w-full pop-mt-6 pop-px-6'}>
					{props.bestSize.label !== 'error' && props.bestSize.accuracy && (
						<div>
							<PrimaryParagraph className={'pop-text-lg pop-text-center pop-my-1 pop-font-semibold'}>{t('user_my_trophies_accuracy')}	</PrimaryParagraph>
							<PrimaryParagraph className={'pop-text-sm pop-text-center'}>
							{t('size_accuracy_message_1')} <span className={'pop-italic pop-font-semibold'}>{t('size_accuracy_message_2')}</span> {t('size_accuracy_message_3')}
							</PrimaryParagraph>
							<div className={'pop-flex pop-justify-center pop-mt-6'}>
								<PrimaryParagraph
									className={
										'pop-text-lg pop-text-center pop-font-semibold pop-rounded-full pop-border-4 pop-border-popsizeSecondary90 pop-w-20 pop-h-20 pop-flex pop-items-center pop-justify-center'
									}
								>
									{Math.floor(props.bestSize.accuracy * 100)}%
								</PrimaryParagraph>
							</div>
							{!isAuthenticated && (
								<SecondaryButton
									className="pop-w-full pop-bg-popsizeGradientSecondary9050 pop-border-none pop-text-white pop-mt-4"
									onClick={() => {
										trackEvent('save_profile_clicked');
										props.handleSaveClick?.();
									}}
								>
									{t('save_my_size')}
								</SecondaryButton>
							)}
						</div>
					)}
					{isAuthenticated && (
						<div className={'pop-flex pop-justify-center pop-mt-8'} onClick={() => {
							trackEvent('user_logged_out');
							props.handleLogout?.();
						}}>
							<PrimaryParagraph
								className={
									'pop-text-sm pop-text-center pop-flex pop-items-center pop-justify-center hover:pop-underline pop-cursor-pointer'
								}
							>
								{t('user_log_out')}
							</PrimaryParagraph>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SizeRecommandationView;
